import React from "react";
import { CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { formatGBP } from "@/lib/brand";
import { regularHeader, monthNames, annualTotal, contingencyAmount } from "@/lib/costHelpers";
import { streamHeader, streamMonth, followsRhythm, RHYTHM_OPTIONS } from "@/lib/incomeHelpers";
import { goalMonth, GOAL_KINDS } from "@/components/onboarding/GoalsStep";
import { LEGAL_ENTITY_OPTIONS, EMPLOYEES_OPTIONS } from "@/components/onboarding/BasicsSteps";

const ReviewCard = ({ label, stepKey, jumpTo, children }) => (
  <div className="card" data-testid={`review-card-${stepKey}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="section-eyebrow">{label}</div>
      <button type="button" className="btn-secondary !px-3 !py-1.5 !text-xs" onClick={() => jumpTo(stepKey)} data-testid={`review-edit-${stepKey}`}>
        <PencilSimple size={14} /> Edit
      </button>
    </div>
    <div className="text-sm space-y-1">{children}</div>
  </div>
);

const List = ({ items, empty }) =>
  items.length === 0 ? <div className="text-black/60">{empty}</div> : items.map((t, k) => <div key={k}>• {t}</div>);

const Total = ({ children }) => <div className="mt-2 text-black/65">{children}</div>;

export const NearlyThereStep = ({ inputs, busy, onFinish, jumpTo }) => {
  const bp = inputs.business_profile;
  const months = monthNames(inputs.start_month, true);
  const costs = inputs.recurring_costs.filter((c) => c.name);
  const oneOffs = inputs.one_off_costs.filter((c) => c.name);
  const streams = inputs.revenue_streams.filter((r) => r.name);
  const goals = inputs.goals.filter((g) => g.title);
  const preset = inputs.seasonality_preset || "steady";
  const excluded = inputs.seasonality_excluded || [];
  const monthlyCosts = costs.reduce((s, c) => s + (c.is_contingency ? contingencyAmount(costs, c) : annualTotal(c) / 12), 0);
  const avgIncome = streams.reduce((s, r) => s + Array.from({ length: 12 }, (_, i) => streamMonth(r, i, inputs.start_month, preset, excluded)).reduce((a, b) => a + b, 0), 0) / 12;
  const rhythmLabel = (RHYTHM_OPTIONS.find((o) => o.k === preset) || RHYTHM_OPTIONS[0]).label;
  const following = streams.filter((r) => followsRhythm(r) && !excluded.includes(r.id)).map((r) => r.name);

  return (
    <div className="space-y-8" data-testid="nearly-there-step">
      <div className="card space-y-4">
        <p className="leading-relaxed text-lg">
          <strong>YOU DID IT!</strong> Look at you: you've just laid the foundation for the next 12 months of your business. Most small business owners never do this. Congratulations!
        </p>
        <p className="leading-relaxed text-black/80">
          This is where you start to truly learn the rhythm of your business, and you won't be doing it alone. We're so excited to go on this journey with you, together with the forecastly community.
        </p>
        <p className="font-semibold">What happens when you press "Build my forecast":</p>
        <ul className="list-disc pl-5 space-y-2 text-black/80 leading-relaxed">
          <li><strong>Your forecast:</strong> your full 12 months mapped out on one screen. Any months highlighted in yellow are where your cash could dip below zero, so you can spot them early and act now. Your AI coach then explains what your numbers mean in a Forecast Story, in plain English.</li>
          <li><strong>Every Monday:</strong> a quick weekly check-in to keep your momentum going, plus an optional live Zoom call with the community.</li>
          <li><strong>Every month: your Money Date.</strong> Log your real numbers on the Actuals page, together with us, live and in real time, with a little accountability. Your AI coach then shows you what's working, what's leaking and what to do next. You don't have to do this alone.</li>
          <li><strong>Your goals:</strong> each one gets a tickable action plan on your Goals page.</li>
        </ul>
        <p className="leading-relaxed text-black/80">Your numbers aren't a verdict. They're a compass.</p>
      </div>

      <div className="space-y-3">
        <div className="section-eyebrow">Have one last look before we build</div>
        <div className="card bg-[#FFF7E5]" data-testid="review-forecast-range">
          <strong>Your forecast covers {months[0]} – {months[11]}</strong>
        </div>
        <ReviewCard label="Business basics" stepKey="business" jumpTo={jumpTo}>
          <div><strong>Business:</strong> {inputs.business_name || "—"}</div>
          {bp.legal_entity && <div><strong>Legal entity:</strong> {LEGAL_ENTITY_OPTIONS.find((o) => o.v === bp.legal_entity)?.label}</div>}
          {bp.employees && <div><strong>People in your business:</strong> {EMPLOYEES_OPTIONS.find((o) => o.v === bp.employees)?.label}</div>}
          <div><strong>VAT registered:</strong> {{ yes: "Yes", no: "No" }[bp.vat_registered] || "Not answered"}</div>
          {bp.industry && <div><strong>Industry:</strong> {bp.industry}</div>}
          {bp.biggest_challenge && <div><strong>Biggest challenge:</strong> {bp.biggest_challenge}</div>}
        </ReviewCard>
        <ReviewCard label="Opening cash" stepKey="opening_cash" jumpTo={jumpTo}>
          <div>
            Total opening cash on 1 {months[0]}: <strong>{formatGBP(inputs.opening_cash)}</strong>
            {Number(inputs.opening_cash) < 0 && <span className="text-black/65"> (overdrawn)</span>}
          </div>
        </ReviewCard>
        <ReviewCard label="Regular costs" stepKey="recurring_costs" jumpTo={jumpTo}>
          <List items={costs.map((c, i) => regularHeader(c, i, inputs.start_month, costs))} empty="No regular costs added." />
          <Total>Average monthly regular costs: <strong>{formatGBP(monthlyCosts)}</strong></Total>
        </ReviewCard>
        <ReviewCard label="One-off costs" stepKey="one_off" jumpTo={jumpTo}>
          <List items={oneOffs.map((c) => `${c.name} · ${months[Number(c.month_index) || 0]} · ${formatGBP(Number(c.amount) || 0)}`)} empty="None yet." />
          <Total>Total one-offs over 12 months: <strong>{formatGBP(oneOffs.reduce((s, c) => s + (Number(c.amount) || 0), 0))}</strong></Total>
        </ReviewCard>
        <ReviewCard label="Paying yourself" stepKey="personal_draw" jumpTo={jumpTo}>
          <div><strong>{formatGBP(inputs.personal_draw)}</strong> / month</div>
        </ReviewCard>
        <ReviewCard label="Income streams" stepKey="revenue" jumpTo={jumpTo}>
          <List items={streams.map((r, i) => streamHeader(r, i, inputs.start_month))} empty="No income streams added yet." />
          <Total>Average monthly income: <strong>{formatGBP(avgIncome)}</strong></Total>
        </ReviewCard>
        <ReviewCard label="Your rhythm" stepKey="seasonality" jumpTo={jumpTo}>
          <div><strong>{rhythmLabel}</strong>{preset === "custom" && inputs.seasonality_note ? `: ${inputs.seasonality_note}` : ""}</div>
          {preset !== "steady" && preset !== "custom" && <div className="text-black/65">Followed by: {following.length ? following.join(", ") : "none of your income streams"}</div>}
        </ReviewCard>
        <ReviewCard label="12-month plans" stepKey="goals" jumpTo={jumpTo}>
          <List
            items={goals.map((g) => [g.title, (GOAL_KINDS.find((k) => k.v === g.kind) || GOAL_KINDS[0]).label, goalMonth(g, inputs.revenue_streams, inputs.start_month)].filter(Boolean).join(" · "))}
            empty="None yet. No problem, you can add these later."
          />
        </ReviewCard>
      </div>

      <button className="btn-primary w-full justify-center text-lg py-4 mt-4" onClick={onFinish} disabled={busy} data-testid="onboarding-finish-btn">
        {busy ? "Building your forecast…" : "Build my forecast"}
        <CheckCircle size={20} weight="fill" />
      </button>
    </div>
  );
};

export default NearlyThereStep;
